import React, { ReactNode, useMemo } from "react";
import { a } from "kremling";
import { useHighlighter } from "@hooks";
import { ItemBase } from "./item-base";
import { CpNameChip } from "@components";
import { TruncateText } from "../common/truncate-text.component";
import { RenderIconDynamic } from "../../cp-icon/render-icon-dynamic";
import { always, maybe } from "../../../utils";
import { SelectDataItem } from "../common/utils";

export type ItemMultiProps = {
  isSelected?: boolean;
  item: SelectDataItem & {
    viewOnly?: boolean;
    icon?: string | ReactNode;
    iconColor?: string;
    namechip?: any;
    overline?: string;
  };
  value: SelectDataItem[];
  onChange?: (item: SelectDataItem, isMulti?: boolean) => void;
  searchValue?: string;
  allowTextWrap?: boolean;
};

export const ItemMulti = (props: ItemMultiProps) => {
  const { isSelected, item, value, onChange, searchValue, allowTextWrap } =
    props;
  const { viewOnly } = item;
  const isChecked = useMemo(() => {
    return value.findIndex((v) => v.id === item.id) > -1;
  }, [value, item]);

  const isDisabled = item.disabled;
  const onCheckBoxChange = () => {
    if (!isDisabled && !viewOnly) {
      onChange?.(item, true);
    }
  };

  const name = useHighlighter(item.name, searchValue);
  const subName = useHighlighter(item.subName, searchValue);

  return (
    <ItemBase
      id={item.id}
      isSelected={isSelected}
      className={a("item-multi")
        .m("item-multi--selected", isSelected)
        .m("item-multi--disabled", isDisabled)
        .m("item-multi-cursor", viewOnly)}
      subHeader={item.subHeader}
      onClick={onCheckBoxChange}
    >
      <div className="item-multi__display-content">
        <div className="cp-flex-center">
          {item.icon && (
            <div className="cp-mr-8 flex items-center">
              <RenderIconDynamic
                icon={item.icon}
                fill={
                  item.disabled
                    ? "var(--cp-color-app-disabled-text)"
                    : item.iconColor || "var(--cp-color-app-icon)"
                }
              />
            </div>
          )}
          {item.namechip && (
            <CpNameChip className="cp-mr-8" size="medium" {...item.namechip} />
          )}
          <div className={maybe(!allowTextWrap, "cp-ellipsis")}>
            {item.overline && (
              <div
                className={always(
                  "item-multi__display-sub",
                  maybe(!allowTextWrap, "cp-ellipsis"),
                )}
              >
                {item.overline}
              </div>
            )}
            <div
              className={always(
                maybe(!allowTextWrap, "item-multi__display"),
                maybe(!allowTextWrap, "cp-ellipsis"),
              )}
            >
              {name}
            </div>
            {item.subName &&
              (allowTextWrap ? (
                <div className="item-multi__display-sub">{subName}</div>
              ) : (
                <TruncateText
                  text={subName}
                  className="item-multi__display-sub"
                />
              ))}
          </div>
        </div>
      </div>
      {!viewOnly && (
        <div>
          <label className="cps-checkbox">
            <input
              tabIndex={-1}
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled}
              readOnly
            />
            <span />
          </label>
        </div>
      )}
    </ItemBase>
  );
};
